<script setup lang="ts">
import { avatarText } from '@core/utils/formatters'
import { take, words } from 'lodash'
import type { ChangeRequestSubtask, SubtaskStatusOption, SubtaskTypeOption } from '@/models/changeRequest'
import { useSnackbarStore } from '@/store/snackbar'
import type { VForm } from 'vuetify/components'
import { requiredValidator } from '@validators'
import { $duplicateChangeRequestSubtask } from '@/api/changeRequests'

interface IProps {
  requestSubtask?: ChangeRequestSubtask | null,
  showSelectUsersDialog?: boolean,
  changeRequestLoading?: boolean,
  editSubtaskId?: number | null,
  subtaskStatusOptions?: SubtaskStatusOption[],
  subtaskTypeOptions?: SubtaskTypeOption[],
  hideDeleteBtn?: boolean,
  hideDuplicateBtn?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  requestSubtask: null,
  showSelectUsersDialog: false,
  changeRequestLoading: false,
  editSubtaskId: null,
  subtaskStatusOptions: () => ([]),
  hideDeleteBtn: false,
  hideDuplicateBtn: false,
})

interface Emit {
  (e: 'update:requestSubtask', value:  ChangeRequestSubtask | null): void
  (e: 'update:showSelectUsersDialog', value:  boolean): void
  (e: 'update:editSubtaskId', value:  number | null): void
  (e: 'onCreateChangeRequestSubtask'): void
  (e: 'onUpdateChangeRequestSubtask'): void
  (e: 'onDeleteChangeRequestSubtask'): void
  (e: 'onDuplicateChangeRequestSubtask'): void
  (e: 'onAddChangeRequestSubtaskComment'): void
}

const emit = defineEmits<Emit>()

// Inject from Change Request View page (change-requests/[id]/index.vue) - changeRequestProjectId prop
const changeRequestProjectId = inject<number | null | undefined>('changeRequestProjectId', null)

const refVForm = ref<VForm>()

const duplicateChangeRequestSubtaskLoading = ref<boolean>(false)

const showChangeRequestSubtaskCommentsDialog = ref<boolean>(false)

const snackbarStore = useSnackbarStore()

const computedSubtask = computed({
  // getter
  get: () => props.requestSubtask || {},
  // setter
  set: (newRequestSubtask: ChangeRequestSubtask | null) => {
    emit('update:requestSubtask', newRequestSubtask) // Emit update to parent when the local Subtask is changed
  }
})

const isViewSubtask = computed(() => {
  if (props.requestSubtask?.id) {
    return props.editSubtaskId !== props.requestSubtask?.id
  } else return false
})

// Remove assignedToUser from the list
const removeAssignedToUser = (assignedToUserKey: number) => {
  if (!computedSubtask.value?.assigned_to_users_info?.length) return
  computedSubtask.value.assigned_to_users_info.splice(assignedToUserKey, 1)
}

const duplicateChangeRequestSubtask = async (subtaskId: number) => {
  if (!subtaskId) return

  duplicateChangeRequestSubtaskLoading.value = true
  try {
    await $duplicateChangeRequestSubtask({ id: subtaskId })

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Change Request Subtask duplicated successfully!'
    })

    emit('onDuplicateChangeRequestSubtask')
  } catch (e: any) {
    console.log(e)
  } finally {
    duplicateChangeRequestSubtaskLoading.value = false
  }
}

const onSubmit = async () => {
  const isValid = await refVForm.value?.validate()
  if (!isValid || !isValid.valid) {
    return
  }

  props.requestSubtask?.id ? emit('onUpdateChangeRequestSubtask') : emit('onCreateChangeRequestSubtask')
}
</script>

<template>
  <div class="change-request-subtask-wrapper">
    <ChangeRequestSubtaskCommentsDialog
      v-model="showChangeRequestSubtaskCommentsDialog"
      :change-request-subtask-id="computedSubtask?.id"
      @onAddComment="emit('onAddChangeRequestSubtaskComment')"
    />
    <h4 class="px-1 pt-2 pb-1 d-flex align-center flex-wrap">
      {{ requestSubtask?.id ? `Subtask ${requestSubtask?.code}` : 'Add a new subtask' }}
      <VBtn
        v-if="isViewSubtask"
        size=""
        class="pa-1 ml-1"
        :disabled="!!editSubtaskId"
        @click="emit('update:editSubtaskId', requestSubtask?.id)"
      >
        <VIcon icon="tabler-pencil" />
      </VBtn>
      <VBtn
        v-if="isViewSubtask && !hideDuplicateBtn"
        size=""
        class="pa-1 position-relative ml-1 ml-sm-auto"
        :disabled="!!duplicateChangeRequestSubtaskLoading"
        @click="duplicateChangeRequestSubtask(requestSubtask?.id)"
      >
        <VOverlay
          :model-value="!!duplicateChangeRequestSubtaskLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            size="16"
            width="3"
            indeterminate
            color="white"
          />
        </VOverlay>
        <VIcon icon="tabler-copy" />
        <VMenu
          activator="parent"
          location="top center"
          open-on-hover
          open-delay="100"
          close-delay="0"
          offset="5"
          class="default-tooltip"
        >
          <span class="px-2">
            Duplicate Subtask
          </span>
        </VMenu>
      </VBtn>
    </h4>
    <VForm
      ref="refVForm"
      @submit.prevent="onSubmit"
    >
      <VTable class="change-request-subtask-table">
        <tbody>
          <tr>
            <td class="px-1 py-1">
              <span>
                Subtask Type
              </span>
              <span v-if="!isViewSubtask" class="text-primary ml-1">*</span>
            </td>
            <td class="px-1 py-1">
              <span v-if="isViewSubtask">
                {{ requestSubtask?.subtask_type_display }}
              </span>
              <VSelect
                v-else
                v-model="computedSubtask.subtask_type"
                :disabled="!!changeRequestLoading"
                :items="subtaskTypeOptions"
                item-title="display_name"
                item-value="value"
                label="Subtask Type"
                :rules="[requiredValidator]"
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 pt-2 pb-1">
              <span>
                Description
              </span>
            </td>
            <td class="px-1 pt-2 pb-1">
              <span v-if="isViewSubtask">
                {{ requestSubtask?.description }}
              </span>
              <VTextarea
                v-else
                v-model="computedSubtask.description"
                :disabled="!!changeRequestLoading"
                name="Description"
                label="Description"
                type="text"
                rows="2"
              />
            </td>
          </tr>
          <tr>
            <td class="pa-1">
              <span>
                Assigned To
              </span>
            </td>
            <td class="pa-1">
              <VAvatar
                v-for="(assignedToUser, assignedToUserKey) in computedSubtask?.assigned_to_users_info"
                :key="assignedToUserKey"
                rounded
                :size="25"
                color="primary"
                variant="tonal"
                class="ma-1 cursor-pointer"
              >
                <span
                  class="text-sm-center font-weight-medium"
                >
                  {{ avatarText(take(words(assignedToUser?.display_name), 2).join(' ')) }}
                </span>
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-click
                  close-on-content-click
                  close-on-back
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <div class="d-flex align-center justify-center px-2">
                    <span>{{ assignedToUser?.display_name }}</span>
                    <VBtn
                      v-if="!isViewSubtask"
                      size=""
                      class="pa-1"
                      color="light"
                      :disabled="!!changeRequestLoading"
                      @click="removeAssignedToUser(assignedToUserKey)"
                    >
                      <VIcon color="primary" icon="mdi-minus-circle" />
                    </VBtn>
                  </div>
                </VMenu>
              </VAvatar>
              <VBtn
                v-if="!isViewSubtask"
                size=""
                color="light"
                class="border px-1 ma-1"
                :disabled="!!changeRequestLoading"
                @click="emit('update:showSelectUsersDialog', true)"
              >
                <VIcon
                  size="22"
                  icon="mdi-plus"
                />
              </VBtn>
            </td>
          </tr>
          <tr>
            <td class="pa-1">
              <span>
                Status
              </span>
            </td>
            <td class="pa-1">
              <span v-if="isViewSubtask">
                {{ requestSubtask?.status_display }}
              </span>
              <VSelect
                v-else
                v-model="computedSubtask.status"
                :disabled="!!changeRequestLoading"
                :items="subtaskStatusOptions"
                item-title="display_name"
                item-value="value"
                label="Status"
              />
            </td>
          </tr>
          <tr>
            <td class="pa-1">
              <span>
                Due Date
              </span>
            </td>
            <td class="pa-1">
               <span v-if="isViewSubtask">
                {{ requestSubtask?.due_date }}
              </span>
              <!-- Use separate disabled AppDateTimePicker due to disabled property limitations -->
              <AppDateTimePicker
                v-else-if="!!changeRequestLoading"
                :model-value="computedSubtask?.due_date"
                :config="{ dateFormat: 'Y-m-d' }"
                placeholder="Due Date"
                disabled
              />
              <!-- /Use separate disabled AppDateTimePicker due to disabled property limitations -->
              <AppDateTimePicker
                v-else
                v-model="computedSubtask.due_date"
                :config="{ dateFormat: 'Y-m-d' }"
                clearable
                placeholder="Due Date"
              />
            </td>
          </tr>
          <tr>
            <td class="px-1 pt-1 pb-2">
              <span>
                Notes
              </span>
            </td>
            <td class="px-1 pt-1 pb-2">
              <VChip
                size="small"
                :color="computedSubtask?.comments_count ? 'primary' : 'default'"
                variant="outlined"
              >
                {{ computedSubtask?.comments_count || 0 }}
              </VChip>

              <VBtn
                v-if="isViewSubtask && computedSubtask?.id"
                size=""
                class="pa-1 ml-1"
                @click="showChangeRequestSubtaskCommentsDialog = true"
              >
                <VIcon icon="tabler-message" />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <span class="px-2">
                    View/Add Notes
                  </span>
                </VMenu>
              </VBtn>
            </td>
          </tr>
        </tbody>
      </VTable>
      <div
        v-if="!isViewSubtask"
        class="d-flex align-center justify-space-between"
      >
        <div>
          <VBtn
            class="ma-1"
            size="small"
            color="success"
            :disabled="!!changeRequestLoading"
            type="submit"
          >
            <VProgressCircular
              v-if="!!changeRequestLoading"
              class="mr-1"
              size="16"
              width="2"
              indeterminate
              color="white"
            />
            {{ requestSubtask?.id ? 'Update' : 'Add' }}
          </VBtn>
          <VBtn
            class="ma-1"
            size="small"
            color="secondary"
            :disabled="!!changeRequestLoading"
            @click="emit('update:editSubtaskId', null); computedSubtask = null;"
          >
            Cancel
          </VBtn>
        </div>
        <VBtn
          v-if="requestSubtask?.id && !hideDeleteBtn"
          class="my-1 mr-1 ml-auto"
          size="small"
          color="primary"
          :disabled="!!changeRequestLoading"
          @click="emit('onDeleteChangeRequestSubtask', null)"
        >
          Delete
        </VBtn>
      </div>
    </VForm>
  </div>
</template>

<style lang="scss">
.change-request-subtask-wrapper {
  border: 1px solid #4d77de;
  border-radius: 25px;
  padding: 10px 20px;
  .change-request-subtask-table {
    tr {
      td:first-child {
        width: 30%;
      }
      td {
        height: unset !important;
        border-bottom: none !important;
      }
    }
  }
}
</style>
